import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit2, Trash2, Save, X, FileDown, Eye, EyeOff } from 'lucide-react';

const Calificaciones = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [calificaciones, setCalificaciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clases, setClases] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [estudiantesClase, setEstudiantesClase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCalificacion, setNewCalificacion] = useState({
    estudiante_id: '',
    clase_id: '',
    tarea_id: '',
    calificacion: '',
    comentarios: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all'); // all, my-grades (for students)
  const [editingGrade, setEditingGrade] = useState(null);
  const [editData, setEditData] = useState({ 
    estudiante_id: '',
    clase_id: '',
    tarea_id: '',
    calificacion: '', 
    comentarios: '' 
  });
  const [updating, setUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // For visibility controls
  const [loadingClases, setLoadingClases] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [registrandoCapacitacion, setRegistrandoCapacitacion] = useState(null); // ID del estudiante siendo registrado
  
  // Modal para registrar capacitación con horas
  const [modalCapacitacion, setModalCapacitacion] = useState(null); // {profesorId, claseId, profesorNombre, promedio}
  const [horasCapacitacion, setHorasCapacitacion] = useState('');
  
  // For admin class filter (multiple selection)
  const [selectedClases, setSelectedClases] = useState([]);
  const [filtroEstadoAdmin, setFiltroEstadoAdmin] = useState('activa'); // activa, inactiva - default activa
  const [mostrarTodasClases, setMostrarTodasClases] = useState(true); // true = mostrar todas, false = seleccionar
  
  // For student filters - Year and Semester/Class Type
  const [filtroAnio, setFiltroAnio] = useState('todos');
  const [filtroSemestre, setFiltroSemestre] = useState('todos');
  
  // For professor filters - Type and Status
  const [filtroTipoVista, setFiltroTipoVista] = useState('clases_dicto');
  const [filtroEstado, setFiltroEstado] = useState('activa'); // Por defecto mostrar solo activas

  // Cartas de advertencia: índice { "<estudiante_id>_<clase_id>": [cartas...] }
  const [cartasIndex, setCartasIndex] = useState({});
  // Modal de confirmación antes de enviar carta
  const [confirmCarta, setConfirmCarta] = useState(null); // {estudianteId, claseId, estudianteNombre, email, claseNombre, promedio}
  const [enviandoCarta, setEnviandoCarta] = useState(false);
  // Modal con historial de cartas enviadas a un estudiante/clase
  const [historialCartas, setHistorialCartas] = useState(null); // {estudianteNombre, claseNombre, items: []}

  useEffect(() => {
    loadData();
    loadClases();
    loadCartasIndex();
  }, []);
  
  // Reload data when professor filters change
  useEffect(() => {
    if (user.rol === 'profesor') {
      loadData();
    }
  }, [filtroTipoVista, filtroEstado]);
  
  // Reload data when admin filters change
  useEffect(() => {
    if (user.rol === 'admin') {
      loadData();
    }
  }, [filtroEstadoAdmin]);

  const loadData = async () => {
    try {
      // For students, formularios endpoint returns 403, so we handle it separately
      const clasesPromise = axios.get(`${API}/clases`);
      const tareasPromise = axios.get(`${API}/tareas`);
      
      // Build calificaciones URL with filters for professors and admin
      let calificacionesUrl = `${API}/calificaciones`;
      if (user.rol === 'profesor') {
        const params = new URLSearchParams();
        if (filtroTipoVista) {
          params.append('tipo_vista', filtroTipoVista);
        }
        if (filtroEstado && filtroEstado !== 'todas') {
          params.append('estado', filtroEstado);
        }
        if (params.toString()) {
          calificacionesUrl += `?${params.toString()}`;
        }
      } else if (user.rol === 'admin') {
        const params = new URLSearchParams();
        if (filtroEstadoAdmin) {
          params.append('estado', filtroEstadoAdmin);
        }
        if (params.toString()) {
          calificacionesUrl += `?${params.toString()}`;
        }
      }
      
      const calificacionesPromise = axios.get(calificacionesUrl);
      
      // Only load formularios for admin/profesor
      const formulariosPromise = (user.rol === 'admin' || user.rol === 'profesor') 
        ? axios.get(`${API}/formularios`)
        : Promise.resolve({ data: [] });
      
      // Only load users for admin
      const usuariosPromise = (user.rol === 'admin') 
        ? axios.get(`${API}/users`)
        : Promise.resolve({ data: [] });

      const [clasesRes, tareasRes, formulariosRes, calificacionesRes, usuariosRes] = await Promise.all([
        clasesPromise,
        tareasPromise,
        formulariosPromise,
        calificacionesPromise,
        usuariosPromise
      ]);
      
      setClases(clasesRes.data);
      setTareas(tareasRes.data);
      setFormularios(formulariosRes.data);
      setCalificaciones(calificacionesRes.data);
      
      if (user.rol === 'admin') {
        setUsuarios(usuariosRes.data);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Helper para verificar si es estudiante o pastor (mismo comportamiento)
  const isStudentRole = (rol) => ['estudiante', 'pastor', 'graduado'].includes(rol);

  const loadClases = async () => {
    if (isStudentRole(user.rol)) return; // Students/Pastors don't need class controls
    
    setLoadingClases(true);
    try {
      const response = await axios.get(`${API}/clases`);
      setClases(response.data);
    } catch (error) {
      toast.error('Error al cargar clases');
    } finally {
      setLoadingClases(false);
    }
  };

  // Function to load students for a specific class
  const loadEstudiantesClase = async (claseId) => {
    if (!claseId) {
      setEstudiantesClase([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/clases/${claseId}/estudiantes`);
      setEstudiantesClase(response.data);
    } catch (error) {
      console.error('Error loading class students:', error);
      toast.error('Error al cargar estudiantes de la clase');
      setEstudiantesClase([]);
    }
  };

  // Helper function to get students enrolled in selected class (fallback for admin)
  const getEstudiantesInscritosEnClase = (claseId) => {
    if (!claseId) return [];
    
    // Use estudiantesClase if available (for professors), otherwise fallback to usuarios (for admin)
    if (estudiantesClase.length > 0) {
      return estudiantesClase;
    }
    
    const clase = clases.find(c => c.id === claseId);
    if (!clase || !clase.estudiantes_inscritos) return [];
    
    return usuarios.filter(usuario => 
      ['estudiante', 'pastor', 'graduado'].includes(usuario.rol) && 
      clase.estudiantes_inscritos.includes(usuario.id)
    );
  };

  const toggleCalificacionesVisibility = async (claseId, currentVisibility) => {
    setTogglingVisibility(claseId);
    try {
      const response = await axios.put(`${API}/clases/${claseId}/calificaciones-visibilidad`, {
        calificaciones_visibles: !currentVisibility
      });
      
      if (response.data.success) {
        // Update clases state
        setClases(clases.map(clase => 
          clase.id === claseId 
            ? { ...clase, calificaciones_visibles: !currentVisibility }
            : clase
        ));
        
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar visibilidad');
    } finally {
      setTogglingVisibility(null);
    }
  };

  // Abrir modal para registrar capacitación
  const abrirModalCapacitacion = (profesorId, claseId, profesorNombre, promedio) => {
    setModalCapacitacion({ profesorId, claseId, profesorNombre, promedio });
    setHorasCapacitacion('');
  };

  // Registrar capacitación en el perfil del profesor
  const handleRegistrarCapacitacionPerfil = async () => {
    if (!modalCapacitacion) return;
    
    const { profesorId, claseId, profesorNombre, promedio } = modalCapacitacion;
    const horas = horasCapacitacion ? parseInt(horasCapacitacion) : null;
    
    if (!horas || horas <= 0) {
      toast.error('Por favor ingresa las horas de la capacitación');
      return;
    }
    
    setRegistrandoCapacitacion(profesorId);
    try {
      const clase = clases.find(c => c.id === claseId);
      if (!clase) {
        toast.error('No se encontró información de la clase');
        return;
      }

      const response = await axios.post(`${API}/admin/registrar-capacitacion-perfil`, {
        profesor_id: profesorId,
        clase_id: claseId,
        nombre: clase.titulo,
        instructor: clase.profesor_nombre,
        fecha_inicio: clase.fecha_inicio,
        fecha_finalizacion: clase.fecha_fin,
        calificacion: parseFloat(promedio),
        horas: horas
      });

      if (response.data.success) {
        toast.success(`✅ Capacitación de ${horas} horas registrada en el perfil de ${profesorNombre}`);
        setModalCapacitacion(null);
        setHorasCapacitacion('');
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Esta capacitación ya está registrada en el perfil del profesor');
      } else {
        toast.error(error.response?.data?.detail || 'Error al registrar capacitación');
      }
    } finally {
      setRegistrandoCapacitacion(null);
    }
  };

  const handleCreateCalificacion = async (e) => {
    e.preventDefault();
    if (!newCalificacion.estudiante_id || !newCalificacion.clase_id || !newCalificacion.calificacion) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    if (parseFloat(newCalificacion.calificacion) < 0 || parseFloat(newCalificacion.calificacion) > 100) {
      toast.error('La calificación debe estar entre 0 y 100');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/calificaciones`, {
        ...newCalificacion,
        calificacion: parseFloat(newCalificacion.calificacion),
        tarea_id: (newCalificacion.tarea_id && newCalificacion.tarea_id !== 'none') ? newCalificacion.tarea_id : null
      });
      
      setCalificaciones([response.data, ...calificaciones]);
      setNewCalificacion({
        estudiante_id: '',
        clase_id: '',
        tarea_id: '',
        calificacion: '',
        comentarios: ''
      });
      setIsDialogOpen(false);
      toast.success('Calificación registrada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar calificación');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getGradeColor = (grade) => {
    if (grade >= 70) return 'text-green-600 bg-green-100';  // Aprobado
    return 'text-red-600 bg-red-100';  // No Aprobado
  };

  const getGradeStatus = (grade) => {
    if (grade >= 70) return { text: 'Aprobado', icon: 'fas fa-check-circle', color: 'text-green-600' };
    return { text: 'No Aprobado', icon: 'fas fa-times-circle', color: 'text-red-600' };
  };

  const calculateAverage = (studentGrades) => {
    if (studentGrades.length === 0) return 0;
    
    // Agrupar calificaciones por clase
    const clasesMap = {};
    studentGrades.forEach(grade => {
      const claseId = grade.clase_id || 'sin_clase';
      if (!clasesMap[claseId]) {
        clasesMap[claseId] = [];
      }
      clasesMap[claseId].push(grade.calificacion);
    });
    
    // Calcular promedio de cada clase
    const promediosClases = Object.values(clasesMap).map(notas => {
      return notas.reduce((sum, nota) => sum + nota, 0) / notas.length;
    });
    
    // Calcular promedio de promedios
    if (promediosClases.length === 0) return 0;
    const promedioGeneral = promediosClases.reduce((sum, prom) => sum + prom, 0) / promediosClases.length;
    
    return promedioGeneral.toFixed(1);
  };

  // Cargar todas las cartas de advertencia del profesor/admin e indexarlas por
  // "estudianteId_claseId" para saber si hay que mostrar el botón "Cartas enviadas"
  const loadCartasIndex = async () => {
    if (!user || (user.rol !== 'admin' && user.rol !== 'profesor')) return;
    try {
      const response = await axios.get(`${API}/calificaciones/cartas-advertencia`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const idx = {};
      (response.data || []).forEach(carta => {
        const key = `${carta.estudiante_id}_${carta.clase_id}`;
        if (!idx[key]) idx[key] = [];
        idx[key].push(carta);
      });
      setCartasIndex(idx);
    } catch (error) {
      console.error('Error cargando índice de cartas:', error);
    }
  };

  // Paso 1: abrir modal de confirmación
  const handleGenerarCartaAdvertencia = (estudianteId, claseId, estudianteNombre, claseNombre) => {
    // Buscar email del estudiante solo para mostrarlo en el modal; si no lo
    // encontramos localmente, el backend igual lo valida al enviar.
    const usuario = usuarios.find(u => u.id === estudianteId);
    const email = usuario?.email || null;
    setConfirmCarta({
      estudianteId, claseId, estudianteNombre, claseNombre, email
    });
  };

  // Paso 2: confirmar y enviar
  const confirmarEnvioCarta = async () => {
    if (!confirmCarta) return;
    const { estudianteId, claseId, estudianteNombre, email } = confirmCarta;
    setEnviandoCarta(true);
    try {
      const response = await axios.post(
        `${API}/calificaciones/generar-carta-advertencia?estudiante_id=${estudianteId}&clase_id=${claseId}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data?.success) {
        const destino = response.data.enviado_a || email || 'el estudiante';
        toast.success(`Carta enviada a ${destino}`);
        setConfirmCarta(null);
        await loadCartasIndex(); // refrescar índice para que aparezca el botón "Cartas enviadas"
      } else {
        toast.error('No se pudo enviar la carta');
      }
    } catch (error) {
      console.error('Error enviando carta:', error);
      toast.error('Error al enviar la carta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEnviandoCarta(false);
    }
  };

  // Abrir modal con historial + estado de apertura
  const abrirHistorialCartas = async (estudianteId, claseId, estudianteNombre, claseNombre) => {
    try {
      const response = await axios.get(
        `${API}/calificaciones/cartas-advertencia?estudiante_id=${estudianteId}&clase_id=${claseId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setHistorialCartas({
        estudianteNombre, claseNombre,
        items: response.data || []
      });
    } catch (error) {
      console.error('Error cargando historial de cartas:', error);
      toast.error('No se pudo cargar el historial');
    }
  };


  const handleEditGrade = (calificacion) => {
    setEditingGrade(calificacion.id);
    setEditData({
      estudiante_id: calificacion.estudiante_id || '',
      clase_id: calificacion.clase_id || '',
      tarea_id: calificacion.tarea_id || '',
      calificacion: calificacion.calificacion.toString(),
      comentarios: calificacion.comentarios || ''
    });
    
    // Si se selecciona una clase, cargar estudiantes de esa clase
    if (calificacion.clase_id) {
      loadEstudiantesClase(calificacion.clase_id);
    }
  };

  const handleUpdateGrade = async (calificacionId) => {
    if (!editData.calificacion || parseFloat(editData.calificacion) < 0 || parseFloat(editData.calificacion) > 100) {
      toast.error('La calificación debe estar entre 0 y 100');
      return;
    }
    
    if (!editData.estudiante_id || !editData.clase_id) {
      toast.error('Debes seleccionar estudiante y clase');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`${API}/calificaciones/${calificacionId}`, {
        estudiante_id: editData.estudiante_id,
        clase_id: editData.clase_id,
        tarea_id: editData.tarea_id || null,
        calificacion: parseFloat(editData.calificacion),
        comentarios: editData.comentarios
      });
      
      toast.success('Calificación actualizada exitosamente');
      setEditingGrade(null);
      loadData(); // Reload data to show changes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar calificación');
    } finally {
      setUpdating(false);
    }
  };

  const cancelEdit = () => {
    setEditingGrade(null);
    setEditData({ 
      estudiante_id: '',
      clase_id: '',
      tarea_id: '',
      calificacion: '', 
      comentarios: '' 
    });
  };

  const handleDeleteGrade = async (calificacionId, estudianteNombre) => {
    if (!window.confirm(`¿Estás seguro que deseas eliminar la calificación de ${estudianteNombre}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/calificaciones/${calificacionId}`);
      toast.success('Calificación eliminada exitosamente');
      loadData(); // Reload data to show changes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar calificación');
    }
  };

  const handleExportExcel = async (claseId = null) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const url = claseId 
        ? `${API}/calificaciones/export/excel?clase_id=${claseId}`
        : `${API}/calificaciones/export/excel`;
      
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      
      // Verify we received a valid response
      if (!response.data || response.data.size === 0) {
        throw new Error('Archivo vacío recibido del servidor');
      }
      
      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Generate timestamp for filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `calificaciones_${timestamp}.xlsx`;
      
      if (contentDisposition) {
        try {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        } catch (e) {
          console.warn('Error parsing filename from headers:', e);
        }
      }
      
      // Create and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      
      // Small delay to ensure the link is in DOM
      setTimeout(() => {
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);
      }, 10);
      
      toast.success(`Archivo Excel generado: ${filename}`, {
        duration: 4000
      });
      
    } catch (error) {
      console.error('Export error:', error);
      
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para exportar calificaciones');
      } else if (error.response?.status === 404) {
        toast.error('No se encontraron calificaciones para exportar');
      } else if (error.message?.includes('vacío')) {
        toast.error('El archivo generado está vacío. Verifica que hay calificaciones registradas.');
      } else {
        toast.error('Error al exportar calificaciones. Intenta nuevamente.');
      }
      
    } finally {
      setExporting(false);
    }
  };

  // Get unique years from calificaciones (for student filter)
  const getAniosDisponibles = () => {
    const years = new Set();
    calificaciones.forEach(cal => {
      if (cal.fecha_calificacion) {
        const year = new Date(cal.fecha_calificacion).getFullYear();
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Newest first
  };

  // Get unique class titles (for student filter)
  const getClasesDisponibles = () => {
    const claseTitulos = new Set();
    calificaciones.forEach(cal => {
      if (cal.clase_titulo) {
        claseTitulos.add(cal.clase_titulo);
      }
    });
    return Array.from(claseTitulos).sort();
  };

  // Filter grades based on user role and selection
  const filteredCalificaciones = calificaciones.filter(cal => {
    if (isStudentRole(user.rol)) {
      // Apply year filter for students
      if (filtroAnio !== 'todos' && cal.fecha_calificacion) {
        const calYear = new Date(cal.fecha_calificacion).getFullYear();
        if (calYear !== parseInt(filtroAnio)) return false;
      }
      
      // Apply class filter for students
      if (filtroSemestre !== 'todos') {
        // Use clase_titulo directly from calificacion
        if (cal.clase_titulo && cal.clase_titulo !== filtroSemestre) {
          return false;
        } else if (!cal.clase_titulo) {
          return false;
        }
      }
      
      return true;
    }
    if (user.rol === 'profesor') {
      // For professors, backend already filters based on tipo_vista and estado
      // So we just return all calificaciones from backend
      return true;
    }
    // Admin: filter by selected classes if not showing all
    if (user.rol === 'admin') {
      // If mostrarTodasClases is false and there are selected classes, filter
      if (!mostrarTodasClases && selectedClases.length > 0) {
        return selectedClases.includes(cal.clase_id);
      }
    }
    return true; // admin sees all if mostrarTodasClases or no filter selected
  });

  // Get clases filtradas por estado para admin (activas/inactivas)
  const clasesFiltradasAdmin = user.rol === 'admin' 
    ? clases.filter(clase => {
        if (filtroEstadoAdmin === 'activa') {
          return clase.activa === true;
        } else if (filtroEstadoAdmin === 'inactiva') {
          return clase.activa === false;
        }
        return true;
      })
    : clases;

  // Group grades by student for better display
  const gradesByStudent = filteredCalificaciones.reduce((acc, cal) => {
    const studentId = cal.estudiante_id;
    if (!acc[studentId]) {
      acc[studentId] = {
        estudiante_nombre: cal.estudiante_nombre,
        calificaciones: []
      };
    }
    acc[studentId].calificaciones.push(cal);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="calificaciones-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? navigate(-1) : navigate('/app/dashboard')}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isStudentRole(user.rol) ? 'Mis Calificaciones' : 'Gestión de Calificaciones'}
            </h1>
            <p className="text-gray-600">
              {isStudentRole(user.rol) 
                ? 'Revisa tu progreso académico y calificaciones'
                : 'Registra y gestiona las calificaciones de los estudiantes'
              }
            </p>
          </div>
        </div>
        
        {/* Class Filter for Admin */}
        {user.rol === 'admin' && (
          <Card className="shadow-md border-l-4 border-l-blue-500 mt-4 lg:mt-0 lg:max-w-xl">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg flex items-center text-blue-800">
                <i className="fas fa-filter mr-2"></i>
                Filtrar Calificaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4">
                {/* Filtro 1: Estado de Clases (Activas/Inactivas) */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    <i className="fas fa-toggle-on mr-2 text-green-600"></i>
                    Estado de Clases
                  </Label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFiltroEstadoAdmin('activa')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                        filtroEstadoAdmin === 'activa'
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-white border border-green-300 text-green-700 hover:bg-green-50'
                      }`}
                    >
                      <i className="fas fa-check-circle mr-1"></i>
                      Activas
                    </button>
                    <button
                      onClick={() => setFiltroEstadoAdmin('inactiva')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                        filtroEstadoAdmin === 'inactiva'
                          ? 'bg-gray-600 text-white shadow-md'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <i className="fas fa-archive mr-1"></i>
                      Inactivas
                    </button>
                  </div>
                </div>

                {/* Filtro 2: Selección de Clases */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    <i className="fas fa-book mr-2 text-blue-600"></i>
                    Clases a Mostrar
                  </Label>
                  
                  {/* Toggle entre Todas y Seleccionar */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => {
                        setMostrarTodasClases(true);
                        setSelectedClases([]);
                      }}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                        mostrarTodasClases
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white border border-blue-300 text-blue-700 hover:bg-blue-50'
                      }`}
                    >
                      <i className="fas fa-list mr-1"></i>
                      Mostrar Todas
                    </button>
                    <button
                      onClick={() => setMostrarTodasClases(false)}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                        !mostrarTodasClases
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-50'
                      }`}
                    >
                      <i className="fas fa-mouse-pointer mr-1"></i>
                      Seleccionar
                    </button>
                  </div>
                  
                  {/* Selector de clases (solo visible si mostrarTodasClases es false) */}
                  {!mostrarTodasClases && (
                    <div className="space-y-3">
                      <Select 
                        value={selectedClases.length === 1 ? selectedClases[0] : ''} 
                        onValueChange={(value) => {
                          if (value === 'select_all') {
                            setSelectedClases(clasesFiltradasAdmin.map(c => c.id));
                          } else if (value === 'clear_all') {
                            setSelectedClases([]);
                          } else {
                            // Toggle selection
                            if (selectedClases.includes(value)) {
                              setSelectedClases(selectedClases.filter(id => id !== value));
                            } else {
                              setSelectedClases([...selectedClases, value]);
                            }
                          }
                        }}
                      >
                        <SelectTrigger className="w-full bg-white">
                          <SelectValue placeholder={
                            selectedClases.length === 0 
                              ? "📚 Selecciona clases..." 
                              : `📚 ${selectedClases.length} clase(s) seleccionada(s)`
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select_all">
                            <div className="flex items-center gap-2 font-semibold text-green-700">
                              <i className="fas fa-check-double"></i>
                              Seleccionar Todas ({clasesFiltradasAdmin.length})
                            </div>
                          </SelectItem>
                          {selectedClases.length > 0 && (
                            <SelectItem value="clear_all">
                              <div className="flex items-center gap-2 font-semibold text-red-600">
                                <i className="fas fa-times"></i>
                                Limpiar Selección
                              </div>
                            </SelectItem>
                          )}
                          <div className="h-px bg-gray-200 my-1"></div>
                          {clasesFiltradasAdmin.map((clase) => (
                            <SelectItem key={clase.id} value={clase.id}>
                              <div className="flex items-center gap-2">
                                {selectedClases.includes(clase.id) ? (
                                  <i className="fas fa-check-square text-green-600"></i>
                                ) : (
                                  <i className="far fa-square text-gray-400"></i>
                                )}
                                <i className={`fas fa-book ${clase.activa ? 'text-blue-600' : 'text-gray-400'}`}></i>
                                <span className="truncate">{clase.titulo}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Selected Classes Pills */}
                      {selectedClases.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-purple-200 max-h-24 overflow-y-auto">
                          {selectedClases.map(claseId => {
                            const clase = clasesFiltradasAdmin.find(c => c.id === claseId);
                            return clase ? (
                              <span
                                key={claseId}
                                className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full"
                              >
                                <span className="truncate max-w-[150px]">{clase.titulo}</span>
                                <button
                                  onClick={() => setSelectedClases(selectedClases.filter(id => id !== claseId))}
                                  className="ml-1 hover:text-red-600 flex-shrink-0"
                                >
                                  <i className="fas fa-times text-xs"></i>
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Filtros aplicados indicator */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Filtros:</span>
                    <Badge variant="secondary" className={`text-xs ${
                      filtroEstadoAdmin === 'activa' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {filtroEstadoAdmin === 'activa' ? '✅ Activas' : '📦 Inactivas'}
                    </Badge>
                    <Badge variant="secondary" className={`text-xs ${
                      mostrarTodasClases ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {mostrarTodasClases 
                        ? `📚 Todas (${clasesFiltradasAdmin.length})` 
                        : `🎯 ${selectedClases.length} seleccionada(s)`
                      }
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {(user.rol === 'admin' || user.rol === 'profesor') && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-4 lg:mt-0">
            <Button
              variant="outline"
              onClick={() => handleExportExcel()}
              disabled={exporting || calificaciones.length === 0}
              className="btn-hover text-sm sm:text-base"
              data-testid="export-excel-button"
            >
              {exporting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  <span className="hidden sm:inline">Exportando...</span>
                  <span className="sm:hidden">Exportando...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel mr-2 text-green-600"></i>
                  <span className="hidden sm:inline">Exportar a Excel</span>
                  <span className="sm:hidden">Exportar</span>
                </>
              )}
            </Button>
            
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEstudiantesClase([]);
                setNewCalificacion({
                  estudiante_id: '',
                  clase_id: '',
                  tarea_id: '',
                  calificacion: '',
                  comentarios: ''
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="instituto-gradient btn-hover text-white text-sm sm:text-base" data-testid="create-calificacion-button">
                  <i className="fas fa-plus mr-2"></i>
                  <span className="hidden sm:inline">Nueva Calificación</span>
                  <span className="sm:hidden">Nueva</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-2xl lg:max-w-3xl max-h-[95vh] overflow-hidden p-0">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 sm:p-6 text-white">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
                    <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                      <i className="fas fa-clipboard-check text-base sm:text-xl"></i>
                    </div>
                    Registrar Nueva Calificación
                  </DialogTitle>
                  <p className="text-emerald-100 text-xs sm:text-sm mt-2">Ingresa la calificación del estudiante</p>
                </DialogHeader>
              </div>
              
              <form onSubmit={handleCreateCalificacion} className="p-3 sm:p-6 space-y-3 sm:space-y-5 overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(95vh-140px)]">
                {/* Selección de Clase */}
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <Label htmlFor="clase_id" className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    <i className="fas fa-graduation-cap text-blue-600 text-sm"></i>
                    Clase <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-2">Selecciona la clase primero</p>
                  <Select value={newCalificacion.clase_id} onValueChange={(value) => {
                    setNewCalificacion({ ...newCalificacion, clase_id: value, estudiante_id: '' });
                    loadEstudiantesClase(value);
                  }}>
                    <SelectTrigger data-testid="calificacion-clase-select" className="mt-1">
                      <SelectValue placeholder="Selecciona una clase" />
                    </SelectTrigger>
                    <SelectContent>
                      {clases
                        .filter(clase => 
                          user.rol === 'admin' || 
                          clase.profesor_id === user.id ||
                          (clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id))
                        )
                        .map((clase) => (
                          <SelectItem key={clase.id} value={clase.id}>
                            <div className="flex items-center gap-2">
                              <i className="fas fa-book text-blue-600"></i>
                              <span className="truncate">{clase.titulo}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Selección de Estudiante */}
                <div className={`rounded-lg p-3 sm:p-4 transition-all ${newCalificacion.clase_id ? 'bg-purple-50 border border-purple-200' : 'bg-gray-100'}`}>
                  <Label htmlFor="estudiante_id" className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    <i className="fas fa-user-graduate text-purple-600 text-sm"></i>
                    Estudiante <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-2">
                    {newCalificacion.clase_id 
                      ? `${getEstudiantesInscritosEnClase(newCalificacion.clase_id).length} inscritos`
                      : 'Selecciona clase primero'
                    }
                  </p>
                  <Select 
                    value={newCalificacion.estudiante_id} 
                    onValueChange={(value) => setNewCalificacion({ ...newCalificacion, estudiante_id: value })}
                    disabled={!newCalificacion.clase_id}
                  >
                    <SelectTrigger data-testid="calificacion-estudiante-select" className="mt-1">
                      <SelectValue placeholder="Selecciona un estudiante" />
                    </SelectTrigger>
                    <SelectContent>
                      {newCalificacion.clase_id ? (
                        getEstudiantesInscritosEnClase(newCalificacion.clase_id)
                          .map((estudiante) => (
                            <SelectItem key={estudiante.id} value={estudiante.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-semibold text-xs">
                                  {estudiante.nombre.charAt(0)}{estudiante.apellido.charAt(0)}
                                </div>
                                {estudiante.nombre} {estudiante.apellido}
                              </div>
                            </SelectItem>
                          ))
                      ) : (
                        <SelectItem value="no_class_selected" disabled>
                          Primero selecciona una clase
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Tarea o Formulario */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <Label htmlFor="tarea_id" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <i className="fas fa-tasks text-amber-600"></i>
                    Tarea o Formulario (Opcional)
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-2">Vincula esta calificación a una tarea o formulario específico</p>
                  <Select value={newCalificacion.tarea_id} onValueChange={(value) => setNewCalificacion({ ...newCalificacion, tarea_id: value })}>
                    <SelectTrigger data-testid="calificacion-tarea-select" className="mt-1">
                      <SelectValue placeholder="Selecciona una tarea o formulario (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-minus-circle text-gray-400"></i>
                          Sin tarea específica
                        </div>
                      </SelectItem>
                      {/* Tareas */}
                      {tareas
                        .filter(tarea => 
                          newCalificacion.clase_id === '' || tarea.clase_id === newCalificacion.clase_id
                        )
                        .map((tarea) => (
                          <SelectItem key={`tarea-${tarea.id}`} value={`tarea-${tarea.id}`}>
                            📝 {tarea.titulo}
                          </SelectItem>
                        ))}
                      {/* Formularios */}
                      {formularios
                        .filter(formulario => 
                          newCalificacion.clase_id === '' || formulario.clase_id === newCalificacion.clase_id
                        )
                        .map((formulario) => (
                          <SelectItem key={`formulario-${formulario.id}`} value={`formulario-${formulario.id}`}>
                            📋 {formulario.titulo}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Calificación y Comentarios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 sm:p-4 border border-green-200">
                    <Label htmlFor="calificacion" className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                      <i className="fas fa-star text-green-600 text-sm"></i>
                      Calificación (0-100) <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-gray-500 mt-1 mb-2">Nota numérica</p>
                    <div className="relative">
                      <Input
                        id="calificacion"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={newCalificacion.calificacion}
                        onChange={(e) => setNewCalificacion({ ...newCalificacion, calificacion: e.target.value })}
                        placeholder="85.5"
                        required
                        data-testid="calificacion-input"
                        className="mt-1 text-lg sm:text-xl font-bold text-center pr-12"
                      />
                      <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">
                        /100
                      </div>
                    </div>
                    {newCalificacion.calificacion && (
                      <div className="mt-2 text-center">
                        <Badge className={`text-xs sm:text-sm
                          ${parseFloat(newCalificacion.calificacion) >= 90 ? 'bg-green-500' : ''}
                          ${parseFloat(newCalificacion.calificacion) >= 70 && parseFloat(newCalificacion.calificacion) < 90 ? 'bg-blue-500' : ''}
                          ${parseFloat(newCalificacion.calificacion) >= 50 && parseFloat(newCalificacion.calificacion) < 70 ? 'bg-yellow-500' : ''}
                          ${parseFloat(newCalificacion.calificacion) < 50 ? 'bg-red-500' : ''}
                          text-white
                        `}>
                          {parseFloat(newCalificacion.calificacion) >= 90 ? '🌟 Excelente' : ''}
                          {parseFloat(newCalificacion.calificacion) >= 70 && parseFloat(newCalificacion.calificacion) < 90 ? '👍 Bueno' : ''}
                          {parseFloat(newCalificacion.calificacion) >= 50 && parseFloat(newCalificacion.calificacion) < 70 ? '📚 Regular' : ''}
                          {parseFloat(newCalificacion.calificacion) < 50 ? '❌ Insuficiente' : ''}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-200">
                    <Label htmlFor="comentarios" className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                      <i className="fas fa-comment-alt text-slate-600 text-sm"></i>
                      Comentarios
                    </Label>
                    <p className="text-xs text-gray-500 mt-1 mb-2">Retroalimentación</p>
                    <Textarea
                      id="comentarios"
                      value={newCalificacion.comentarios}
                      onChange={(e) => setNewCalificacion({ ...newCalificacion, comentarios: e.target.value })}
                      placeholder="Ej: Buen trabajo..."
                      rows={3}
                      data-testid="calificacion-comentarios-input"
                      className="mt-1 resize-none text-sm"
                    />
                  </div>
                </div>
                
                {/* Botones de Acción */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4 border-t">
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium py-2.5 sm:py-3 text-sm sm:text-base"
                    disabled={creating}
                    data-testid="calificacion-submit-button"
                  >
                    {creating ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        <span className="hidden sm:inline">Registrando...</span>
                        <span className="sm:hidden">Guardando...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check-circle mr-2"></i>
                        <span className="hidden sm:inline">Registrar Calificación</span>
                        <span className="sm:hidden">Registrar</span>
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={creating}
                    className="sm:px-6 border-2 hover:bg-gray-100 py-2.5 sm:py-3 text-sm sm:text-base"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>


      {/* Professor Filters */}
      {user.rol === 'profesor' && (
        <Card className="shadow-md border-l-4 border-l-purple-500">
          <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardTitle className="text-lg flex items-center text-purple-800">
              <i className="fas fa-filter mr-2"></i>
              Filtrar Calificaciones
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Tipo de Vista Filter */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  <i className="fas fa-chalkboard-teacher mr-2 text-purple-600"></i>
                  Tipo de Vista
                </Label>
                <Select value={filtroTipoVista} onValueChange={setFiltroTipoVista}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clases_dicto">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chalkboard text-purple-600"></i>
                        <span className="font-medium">📚 Clases que Dicto</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="capacitaciones">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-user-graduate text-green-600"></i>
                        <span className="font-medium">🎓 Mis Capacitaciones</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {filtroTipoVista === 'clases_dicto' 
                    ? 'Ver calificaciones de tus estudiantes'
                    : 'Ver tus propias calificaciones como estudiante'
                  }
                </p>
              </div>

              {/* Estado Filter */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  <i className="fas fa-toggle-on mr-2 text-blue-600"></i>
                  Estado de Clases
                </Label>
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-list text-gray-600"></i>
                        <span className="font-medium">📋 Todas</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="activa">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-check-circle text-green-600"></i>
                        <span className="font-medium">✅ Activas</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="inactiva">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-times-circle text-red-600"></i>
                        <span className="font-medium">❌ Inactivas</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Filtrar por estado de la clase
                </p>
              </div>
            </div>

            {/* Active Filters Indicator */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Filtros aplicados:</span>
                <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                  {filtroTipoVista === 'clases_dicto' ? '📚 Clases que Dicto' : '🎓 Mis Capacitaciones'}
                </Badge>
                <Badge variant="secondary" className={`${
                  filtroEstado === 'activa' ? 'bg-green-100 text-green-800' : 
                  filtroEstado === 'inactiva' ? 'bg-red-100 text-red-800' : 
                  'bg-gray-100 text-gray-800'
                }`}>
                  {filtroEstado === 'todas' ? '📋 Todas' :
                   filtroEstado === 'activa' ? '✅ Activas' : 
                   '❌ Inactivas'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Student View - Show personal grades grouped by class */}
      {isStudentRole(user.rol) && (
        <div className="space-y-6">
          {/* Student Filters */}
          <Card className="shadow-md border-l-4 border-l-blue-500">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg flex items-center text-blue-800">
                <i className="fas fa-filter mr-2"></i>
                Filtrar Calificaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Year Filter */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    <i className="fas fa-calendar mr-2 text-blue-600"></i>
                    Año Académico
                  </Label>
                  <Select value={filtroAnio} onValueChange={setFiltroAnio}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Seleccionar año" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">
                        <span className="font-medium">📅 Todos los años</span>
                      </SelectItem>
                      {getAniosDisponibles().map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Filtra por el año en que recibiste la calificación
                  </p>
                </div>

                {/* Class Filter */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    <i className="fas fa-book mr-2 text-purple-600"></i>
                    Clase/Materia
                  </Label>
                  <Select value={filtroSemestre} onValueChange={setFiltroSemestre}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Seleccionar clase" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">
                        <span className="font-medium">📚 Todas las clases</span>
                      </SelectItem>
                      {getClasesDisponibles().map(clase => (
                        <SelectItem key={clase} value={clase}>
                          {clase}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Filtra por clase específica del curso
                  </p>
                </div>
              </div>

              {/* Active Filters Indicator */}
              {(filtroAnio !== 'todos' || filtroSemestre !== 'todos') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Filtros activos:</span>
                    {filtroAnio !== 'todos' && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        📅 Año: {filtroAnio}
                        <button
                          onClick={() => setFiltroAnio('todos')}
                          className="ml-2 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                    {filtroSemestre !== 'todos' && (
                      <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                        📚 {filtroSemestre}
                        <button
                          onClick={() => setFiltroSemestre('todos')}
                          className="ml-2 hover:text-purple-900"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                    <button
                      onClick={() => {
                        setFiltroAnio('todos');
                        setFiltroSemestre('todos');
                      }}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Limpiar todos
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Student Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <Card className="shadow-md border-0">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <i className="fas fa-chart-line text-blue-600 text-lg sm:text-xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {calculateAverage(filteredCalificaciones)}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">Promedio General</p>
              </CardContent>
            </Card>
            
            <Card className="shadow-md border-0">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <i className="fas fa-award text-green-600 text-lg sm:text-xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {filteredCalificaciones.filter(cal => cal.calificacion >= 80).length}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">Calificaciones Excelentes</p>
              </CardContent>
            </Card>
            
            <Card className="shadow-md border-0 sm:col-span-2 lg:col-span-1">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <i className="fas fa-tasks text-purple-600 text-lg sm:text-xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {filteredCalificaciones.length}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">Total Evaluaciones</p>
              </CardContent>
            </Card>
          </div>

          {/* Student Grades List - Grouped by Class */}
          {(() => {
            // Group grades by class
            const gradesByClass = filteredCalificaciones.reduce((acc, cal) => {
              const claseId = cal.clase_id || 'sin_clase';
              if (!acc[claseId]) {
                acc[claseId] = {
                  clase_nombre: cal.clase_titulo || 'Sin clase',
                  formularios: [],
                  manuales: [],
                  pruebas: []
                };
              }
              
              // Separate between form grades, exam grades, and manual grades
              if (cal.prueba_examen_titulo) {
                acc[claseId].pruebas.push(cal);
              } else if (cal.formulario_titulo) {
                acc[claseId].formularios.push(cal);
              } else {
                acc[claseId].manuales.push(cal);
              }
              
              return acc;
            }, {});

            return Object.keys(gradesByClass).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(gradesByClass).map(([claseId, claseData]) => {
                  const todasCalificaciones = [...claseData.formularios, ...claseData.manuales, ...claseData.pruebas];
                  const promedio = calculateAverage(todasCalificaciones);
                  const promedioNum = parseFloat(promedio);
                  
                  return (
                    <Card key={claseId} className="shadow-lg border-0 border-l-4 border-l-blue-500">
                      <CardHeader className="pb-3 sm:pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <CardTitle className="flex items-center text-lg sm:text-xl text-blue-800">
                            <i className="fas fa-chalkboard-teacher mr-2 sm:mr-3 text-xl sm:text-2xl"></i>
                            <span className="line-clamp-2">{claseData.clase_nombre}</span>
                          </CardTitle>
                          <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
                            <Badge variant="outline" className="text-xs sm:text-sm whitespace-nowrap">
                              {todasCalificaciones.length} evaluaciones
                            </Badge>
                            <Badge 
                              className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${
                                promedioNum >= 70 ? 'bg-green-100 text-green-800 border-green-200' :
                                'bg-red-100 text-red-800 border-red-200'
                              }`}
                            >
                              <i className={`${promedioNum >= 70 ? 'fas fa-check-circle' : 'fas fa-times-circle'} mr-1`}></i>
                              <span className="hidden sm:inline">Promedio: {promedio} {promedioNum >= 70 ? '(Aprobado)' : '(No Aprobado)'}</span>
                              <span className="sm:hidden">{promedio}</span>
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="p-4 sm:p-6">
                        <div className="space-y-4 sm:space-y-6">
                          {/* Formularios Section */}
                          {claseData.formularios.length > 0 && (
                            <div>
                              <h4 className="text-sm sm:text-md font-semibold text-purple-800 mb-3 flex items-center">
                                <i className="fas fa-clipboard-list mr-2 text-sm"></i>
                                <span>Evaluaciones con Formulario ({claseData.formularios.length})</span>
                              </h4>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                                {claseData.formularios
                                  .sort((a, b) => new Date(b.fecha_calificacion) - new Date(a.fecha_calificacion))
                                  .map((calificacion) => {
                                    const status = getGradeStatus(calificacion.calificacion);
                                    return (
                                      <div key={calificacion.id} className="p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                                          <div className="flex-1 min-w-0">
                                            <h5 className="font-semibold text-purple-900 mb-1 text-sm sm:text-base line-clamp-2">
                                              {calificacion.formulario_titulo}
                                            </h5>
                                            {calificacion.tarea_titulo && (
                                              <p className="text-xs text-purple-600 truncate">
                                                <i className="fas fa-tasks mr-1"></i>
                                                {calificacion.tarea_titulo}
                                              </p>
                                            )}
                                            {calificacion.automatica && (
                                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 mt-1">
                                                <i className="fas fa-robot mr-1"></i>
                                                Automática
                                              </Badge>
                                            )}
                                          </div>
                                          <div className={`px-2 sm:px-3 py-1 rounded-full text-base sm:text-lg font-bold flex-shrink-0 ${getGradeColor(calificacion.calificacion)}`}>
                                            {calificacion.calificacion.toFixed(1)}
                                          </div>
                                        </div>
                                        
                                        {calificacion.comentarios && (
                                          <p className="text-sm text-purple-700 mb-2 bg-white bg-opacity-50 p-2 rounded">
                                            <i className="fas fa-comment mr-1"></i>
                                            {calificacion.comentarios}
                                          </p>
                                        )}
                                        
                                        <div className="flex items-center justify-between text-xs text-purple-600 mt-2">
                                          <span>
                                            <i className="fas fa-calendar mr-1"></i>
                                            {formatDate(calificacion.fecha_calificacion)}
                                          </span>
                                          <span className={`font-semibold ${status.color}`}>
                                            <i className={`${status.icon} mr-1`}></i>
                                            {status.text}
                                          </span>
                                        </div>
                                        
                                        {/* Botón para ver respuestas calificadas */}
                                        {calificacion.respuesta_formulario_id && (
                                          <div className="mt-3">
                                            <Button
                                              onClick={() => navigate(`/app/formulario/${calificacion.formulario_id}/respuestas/${calificacion.respuesta_formulario_id}/calificar`)}
                                              variant="outline"
                                              size="sm"
                                              className="w-full bg-purple-100 hover:bg-purple-200 text-purple-800 border-purple-300"
                                            >
                                              <i className="fas fa-eye mr-2"></i>
                                              Ver Mis Respuestas Calificadas
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}

                          {/* Pruebas y Exámenes Section */}
                          {claseData.pruebas.length > 0 && (
                            <div>
                              <h4 className="text-sm sm:text-md font-semibold text-indigo-800 mb-3 flex items-center">
                                <i className="fas fa-file-alt mr-2 text-sm"></i>
                                <span>Pruebas y Exámenes ({claseData.pruebas.length})</span>
                              </h4>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                                {claseData.pruebas
                                  .sort((a, b) => new Date(b.fecha_calificacion) - new Date(a.fecha_calificacion))
                                  .map((calificacion) => {
                                    const status = getGradeStatus(calificacion.calificacion);
                                    return (
                                      <div key={calificacion.id} className="p-3 sm:p-4 bg-indigo-50 border-2 border-indigo-300 rounded-lg hover:shadow-lg transition-shadow">
                                        <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                                          <div className="flex-1 min-w-0">
                                            <h5 className="font-semibold text-indigo-900 mb-1 flex items-center gap-2 text-sm sm:text-base">
                                              <i className="fas fa-clipboard-check text-indigo-600 text-sm flex-shrink-0"></i>
                                              <span className="line-clamp-2">{calificacion.prueba_examen_titulo}</span>
                                            </h5>
                                            <Badge variant="secondary" className="text-xs bg-indigo-200 text-indigo-900 mt-1">
                                              <i className="fas fa-pencil-alt mr-1"></i>
                                              Prueba/Examen
                                            </Badge>
                                          </div>
                                          <div className={`px-2 sm:px-3 py-1 rounded-full text-base sm:text-lg font-bold flex-shrink-0 ${getGradeColor(calificacion.calificacion)}`}>
                                            {calificacion.calificacion.toFixed(1)}
                                            {calificacion.calificacion_sobre && (
                                              <span className="text-xs">/{calificacion.calificacion_sobre}</span>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {calificacion.comentarios && (
                                          <p className="text-xs sm:text-sm text-indigo-700 mb-2 bg-white bg-opacity-50 p-2 rounded break-words">
                                            <i className="fas fa-comment mr-1"></i>
                                            {calificacion.comentarios}
                                          </p>
                                        )}

                                        <div className="mt-2 text-xs text-gray-600 flex items-center justify-between">
                                          <span>
                                            <i className="fas fa-calendar-alt mr-1"></i>
                                            {new Date(calificacion.fecha_calificacion).toLocaleDateString('es-ES')}
                                          </span>
                                          <span className={`flex items-center ${status.color}`}>
                                            <i className={`${status.icon} mr-1`}></i>
                                            {status.text}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}

                          {/* Manual Grades Section */}
                          {claseData.manuales.length > 0 && (
                            <div>
                              <h4 className="text-sm sm:text-md font-semibold text-orange-800 mb-3 flex items-center">
                                <i className="fas fa-pen mr-2 text-sm"></i>
                                <span>Calificaciones Manuales ({claseData.manuales.length})</span>
                              </h4>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                                {claseData.manuales
                                  .sort((a, b) => new Date(b.fecha_calificacion) - new Date(a.fecha_calificacion))
                                  .map((calificacion) => {
                                    const status = getGradeStatus(calificacion.calificacion);
                                    return (
                                      <div key={calificacion.id} className="p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                                          <div className="flex-1 min-w-0">
                                            <h5 className="font-semibold text-orange-900 mb-1 text-sm sm:text-base line-clamp-2">
                                              {calificacion.prueba_examen_titulo || calificacion.tarea_titulo || 'Calificación Manual'}
                                            </h5>
                                          </div>
                                          <div className={`px-2 sm:px-3 py-1 rounded-full text-base sm:text-lg font-bold flex-shrink-0 ${getGradeColor(calificacion.calificacion)}`}>
                                            {calificacion.calificacion.toFixed(1)}
                                          </div>
                                        </div>
                                        
                                        {calificacion.comentarios && (
                                          <p className="text-xs sm:text-sm text-orange-700 mb-2 bg-white bg-opacity-50 p-2 rounded break-words">
                                            <i className="fas fa-comment mr-1"></i>
                                            {calificacion.comentarios}
                                          </p>
                                        )}
                                        
                                        <div className="flex items-center justify-between text-xs text-orange-600 mt-2">
                                          <span>
                                            <i className="fas fa-calendar mr-1"></i>
                                            {formatDate(calificacion.fecha_calificacion)}
                                          </span>
                                          <span className={`font-semibold ${status.color}`}>
                                            <i className={`${status.icon} mr-1`}></i>
                                            {status.text}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="shadow-lg border-0">
                <CardContent className="text-center py-8 sm:py-12 px-4">
                  <i className="fas fa-clipboard-list text-gray-300 text-4xl sm:text-6xl mb-4 sm:mb-6"></i>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No tienes calificaciones aún</h3>
                  <p className="text-sm sm:text-base text-gray-600">Las calificaciones aparecerán aquí cuando tus profesores las registren.</p>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      )}

      {/* Teacher/Admin View - Organized by Class */}
      {(user.rol === 'admin' || user.rol === 'profesor') && (
        <div className="space-y-6">
          {/* Mis Calificaciones (Para Profesores como Estudiantes) */}
          {user.rol === 'profesor' && (() => {
            const misCalificaciones = filteredCalificaciones.filter(cal => cal.estudiante_id === user.id);
            
            if (misCalificaciones.length > 0) {
              // Group by class
              const gradesByClass = misCalificaciones.reduce((acc, cal) => {
                const claseId = cal.clase_id || 'sin_clase';
                if (!acc[claseId]) {
                  acc[claseId] = {
                    clase_nombre: cal.clase_titulo || 'Sin clase',
                    formularios: [],
                    manuales: []
                  };
                }
                
                if (cal.formulario_titulo) {
                  acc[claseId].formularios.push(cal);
                } else {
                  acc[claseId].manuales.push(cal);
                }
                
                return acc;
              }, {});

              return (
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-xl p-4 flex items-center">
                    <i className="fas fa-graduation-cap text-2xl mr-3"></i>
                    <h2 className="text-xl font-bold">Mis Calificaciones (Como Estudiante en Capacitaciones)</h2>
                  </div>
                  <div className="space-y-4 mt-4">
                    {Object.entries(gradesByClass).map(([claseId, claseData]) => {
                      const todasCalificaciones = [...claseData.formularios, ...claseData.manuales];
                      const promedio = calculateAverage(todasCalificaciones);
                      
                      return (
                        <Card key={`mis-${claseId}`} className="shadow-lg border-0 border-l-4 border-l-green-500">
                          <CardHeader className="pb-4 bg-gradient-to-r from-green-50 to-emerald-50">
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center text-lg text-green-800">
                                <i className="fas fa-chalkboard-teacher mr-3"></i>
                                {claseData.clase_nombre}
                              </CardTitle>
                              <Badge className="bg-green-600 text-white">
                                Promedio: {promedio}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="p-6">
                            {/* Formularios */}
                            {claseData.formularios.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-md font-semibold text-green-800 mb-3 flex items-center">
                                  <i className="fas fa-clipboard-list mr-2"></i>
                                  Evaluaciones con Formulario ({claseData.formularios.length})
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {claseData.formularios.map((calificacion) => {
                                    const status = getGradeStatus(calificacion.calificacion);
                                    return (
                                      <div key={calificacion.id} className="p-4 bg-green-50 border border-green-200 rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-3">
                                          <div className="flex-1">
                                            <h5 className="font-semibold text-green-900 mb-1">
                                              {calificacion.formulario_titulo}
                                            </h5>
                                            {calificacion.automatica && (
                                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 mt-1">
                                                <i className="fas fa-robot mr-1"></i>
                                                Automática
                                              </Badge>
                                            )}
                                          </div>
                                          <div className={`px-3 py-1 rounded-full text-lg font-bold ${getGradeColor(calificacion.calificacion)}`}>
                                            {calificacion.calificacion.toFixed(1)}
                                          </div>
                                        </div>
                                        
                                        {calificacion.comentarios && (
                                          <p className="text-sm text-green-700 mb-2 bg-white bg-opacity-50 p-2 rounded">
                                            <i className="fas fa-comment mr-1"></i>
                                            {calificacion.comentarios}
                                          </p>
                                        )}
                                        
                                        <div className="flex items-center justify-between text-xs text-green-600 mt-2">
                                          <span>
                                            <i className="fas fa-calendar mr-1"></i>
                                            {formatDate(calificacion.fecha_calificacion)}
                                          </span>
                                          <span className={`font-semibold ${status.color}`}>
                                            <i className={`${status.icon} mr-1`}></i>
                                            {status.text}
                                          </span>
                                        </div>
                                        
                                        {/* Botón para ver respuestas calificadas */}
                                        {calificacion.respuesta_formulario_id && (
                                          <div className="mt-3">
                                            <Button
                                              onClick={() => navigate(`/app/formulario/${calificacion.formulario_id}/respuestas/${calificacion.respuesta_formulario_id}/calificar`)}
                                              variant="outline"
                                              size="sm"
                                              className="w-full bg-green-100 hover:bg-green-200 text-green-800 border-green-300"
                                            >
                                              <i className="fas fa-eye mr-2"></i>
                                              Ver Mis Respuestas Calificadas
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Manuales */}
                            {claseData.manuales.length > 0 && (
                              <div>
                                <h4 className="text-md font-semibold text-green-800 mb-3 flex items-center">
                                  <i className="fas fa-pen mr-2"></i>
                                  Evaluaciones Manuales ({claseData.manuales.length})
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {claseData.manuales.map((calificacion) => {
                                    const status = getGradeStatus(calificacion.calificacion);
                                    return (
                                      <div key={calificacion.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex-1">
                                            {calificacion.tarea_titulo && (
                                              <h5 className="font-semibold text-green-900">
                                                {calificacion.tarea_titulo}
                                              </h5>
                                            )}
                                          </div>
                                          <div className={`px-3 py-1 rounded-full text-lg font-bold ${getGradeColor(calificacion.calificacion)}`}>
                                            {calificacion.calificacion.toFixed(1)}
                                          </div>
                                        </div>
                                        
                                        {calificacion.comentarios && (
                                          <p className="text-sm text-green-700 mb-2 bg-white bg-opacity-50 p-2 rounded">
                                            <i className="fas fa-comment mr-1"></i>
                                            {calificacion.comentarios}
                                          </p>
                                        )}
                                        
                                        <div className="flex items-center justify-between text-xs text-green-600 mt-2">
                                          <span>
                                            <i className="fas fa-calendar mr-1"></i>
                                            {formatDate(calificacion.fecha_calificacion)}
                                          </span>
                                          <span className={`font-semibold ${status.color}`}>
                                            <i className={`${status.icon} mr-1`}></i>
                                            {status.text}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return null;
          })()}
          
          {/* Calificaciones de mis Estudiantes */}
          {(() => {
            // Filter out professor's own grades
            const calificacionesEstudiantes = user.rol === 'profesor' 
              ? filteredCalificaciones.filter(cal => cal.estudiante_id !== user.id)
              : filteredCalificaciones;
            
            // Group grades by class first, then by student
            const gradesByClass = calificacionesEstudiantes.reduce((acc, cal) => {
              const claseId = cal.clase_id;
              if (!acc[claseId]) {
                const clase = clases.find(c => c.id === claseId);
                acc[claseId] = {
                  clase_nombre: cal.clase_titulo || (clase ? clase.titulo : 'Clase sin título'),
                  estudiantes: {}
                };
              }
              
              const studentId = cal.estudiante_id;
              if (!acc[claseId].estudiantes[studentId]) {
                acc[claseId].estudiantes[studentId] = {
                  estudiante_nombre: cal.estudiante_nombre,
                  formularios: [],
                  manuales: [],
                  pruebas: []
                };
              }
              
              // Separate between form grades, exam grades, and manual grades
              if (cal.prueba_examen_titulo) {
                acc[claseId].estudiantes[studentId].pruebas.push(cal);
              } else if (cal.formulario_titulo) {
                acc[claseId].estudiantes[studentId].formularios.push(cal);
              } else {
                acc[claseId].estudiantes[studentId].manuales.push(cal);
              }
              
              return acc;
            }, {});

            return Object.keys(gradesByClass).length > 0 ? (
              <div className="space-y-8">
                {Object.entries(gradesByClass).map(([claseId, claseData]) => (
                  <Card key={claseId} className="shadow-lg border-0 border-l-4 border-l-blue-500">
                    <CardHeader className="pb-3 sm:pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <CardTitle className="flex items-center text-lg sm:text-xl text-blue-800">
                          <i className="fas fa-chalkboard-teacher mr-2 sm:mr-3 text-xl sm:text-2xl"></i>
                          <span className="line-clamp-2">{claseData.clase_nombre}</span>
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs sm:text-sm whitespace-nowrap">
                            {Object.keys(claseData.estudiantes).length} estudiantes
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportExcel(claseId)}
                            disabled={exporting}
                            className="text-green-600 border-green-200 hover:bg-green-50 text-xs sm:text-sm whitespace-nowrap"
                          >
                            <i className="fas fa-file-excel mr-1 sm:mr-2"></i>
                            <span className="hidden sm:inline">Exportar Clase</span>
                            <span className="sm:hidden">Exportar</span>
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="p-3 sm:p-6">
                      <div className="space-y-4 sm:space-y-6">
                        {Object.entries(claseData.estudiantes).map(([studentId, studentData]) => (
                          <div key={studentId} className="border-l-2 border-gray-200 pl-3 sm:pl-6 relative">
                            {/* Student Header */}
                            <div className="absolute -left-2 top-0 w-3 h-3 sm:w-4 sm:h-4 bg-gray-300 rounded-full"></div>
                            <div className="flex flex-col gap-2 sm:gap-3 mb-3 sm:mb-4">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                                  <i className="fas fa-user-graduate text-gray-600 mr-2 text-sm sm:text-base"></i>
                                  <span className="line-clamp-1">{studentData.estudiante_nombre}</span>
                                </h3>
                                {(() => {
                                  const todasCalificaciones = [...studentData.formularios, ...studentData.manuales, ...studentData.pruebas];
                                  const promedio = calculateAverage(todasCalificaciones);
                                  const promedioNum = parseFloat(promedio);
                                  return (
                                    <Badge 
                                      className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${
                                        promedioNum >= 70 ? 'bg-green-100 text-green-800 border-green-200' :
                                        'bg-red-100 text-red-800 border-red-200'
                                      }`}
                                    >
                                      <i className={`${promedioNum >= 70 ? 'fas fa-check-circle' : 'fas fa-times-circle'} mr-1`}></i>
                                      <span className="hidden sm:inline">Promedio: {promedio} {promedioNum >= 70 ? '(Aprobado)' : '(No Aprobado)'}</span>
                                      <span className="sm:hidden">{promedio}</span>
                                    </Badge>
                                  );
                                })()}
                                <Badge variant="outline" className="text-xs sm:text-sm whitespace-nowrap">
                                  Total: {(studentData.formularios.length + studentData.manuales.length + studentData.pruebas.length)}
                                </Badge>
                              </div>
                              {/* Botones de acción según contexto */}
                              <div className="flex flex-wrap gap-2">
                                {(() => {
                                  const todasCalificaciones = [...studentData.formularios, ...studentData.manuales, ...studentData.pruebas];
                                  const promedio = calculateAverage(todasCalificaciones);
                                  const promedioNum = parseFloat(promedio);
                                  const clase = clases.find(c => c.id === claseId);
                                  const esCapacitacion = clase?.categoria === 'profesores';
                                  
                                  return (
                                    <>
                                      {/* Botón Carta de Advertencia - solo si no aprobó */}
                                      {promedioNum < 70 && todasCalificaciones.length > 0 && (
                                        <Button
                                          size="sm"
                                          onClick={() => handleGenerarCartaAdvertencia(
                                            studentId, 
                                            claseId, 
                                            studentData.estudiante_nombre,
                                            claseData.clase_nombre
                                          )}
                                          className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs"
                                          data-testid={`btn-enviar-carta-${studentId}-${claseId}`}
                                        >
                                          <i className="fas fa-envelope mr-1 sm:mr-2"></i>
                                          <span className="hidden sm:inline">Enviar Carta de Advertencia</span>
                                          <span className="sm:hidden">Advertencia</span>
                                        </Button>
                                      )}
                                      
                                      {/* Botón Cartas enviadas - solo si ya se envió alguna */}
                                      {(cartasIndex[`${studentId}_${claseId}`]?.length > 0) && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => abrirHistorialCartas(
                                            studentId,
                                            claseId,
                                            studentData.estudiante_nombre,
                                            claseData.clase_nombre
                                          )}
                                          className="border-amber-400 text-amber-700 hover:bg-amber-50 text-xs"
                                          data-testid={`btn-cartas-enviadas-${studentId}-${claseId}`}
                                        >
                                          <i className="fas fa-inbox mr-1 sm:mr-2"></i>
                                          <span className="hidden sm:inline">Cartas enviadas ({cartasIndex[`${studentId}_${claseId}`].length})</span>
                                          <span className="sm:hidden">({cartasIndex[`${studentId}_${claseId}`].length})</span>
                                        </Button>
                                      )}
                                      
                                      {/* Botón Registrar en Perfil - solo para capacitaciones de profesores */}
                                      {esCapacitacion && todasCalificaciones.length > 0 && (user.rol === 'admin' || user.rol === 'profesor') && (
                                        <Button
                                          size="sm"
                                          disabled={registrandoCapacitacion === studentId}
                                          onClick={() => abrirModalCapacitacion(
                                            studentId,
                                            claseId,
                                            studentData.estudiante_nombre,
                                            promedio
                                          )}
                                          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs"
                                        >
                                          <i className="fas fa-user-plus mr-1 sm:mr-2"></i>
                                          <span className="hidden sm:inline">Registrar en Perfil</span>
                                          <span className="sm:hidden">A Perfil</span>
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Formularios Section */}
                            {studentData.formularios.length > 0 && (
                              <div className="mb-4 sm:mb-6">
                                <h4 className="text-sm sm:text-md font-medium text-purple-800 mb-2 sm:mb-3 flex items-center">
                                  <i className="fas fa-clipboard-list mr-2 text-sm"></i>
                                  <span>Formularios ({studentData.formularios.length})</span>
                                </h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 ml-2 sm:ml-4">
                                  {studentData.formularios
                                    .sort((a, b) => new Date(b.fecha_calificacion) - new Date(a.fecha_calificacion))
                                    .map((calificacion) => {
                                      const status = getGradeStatus(calificacion.calificacion);
                                      return (
                                        <div key={calificacion.id} className="p-2 sm:p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                          <div className="flex items-start justify-between mb-2 gap-2">
                                            <div className="flex-1 min-w-0">
                                              <h5 className="font-medium text-purple-900 text-xs sm:text-sm line-clamp-2">
                                                {calificacion.formulario_titulo}
                                              </h5>
                                              {calificacion.automatica && (
                                                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 mt-1">
                                                  <i className="fas fa-robot mr-1"></i>
                                                  Automática
                                                </Badge>
                                              )}
                                            </div>
                                            <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold flex-shrink-0 ${getGradeColor(calificacion.calificacion)}`}>
                                              {calificacion.calificacion.toFixed(1)}
                                            </div>
                                          </div>
                                          
                                          {calificacion.comentarios && (
                                            <p className="text-xs text-purple-700 mb-2 break-words">
                                              💬 {calificacion.comentarios}
                                            </p>
                                          )}
                                          
                                          <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs text-purple-600 truncate">
                                              📅 {formatDate(calificacion.fecha_calificacion)}
                                            </p>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleEditGrade(calificacion)}
                                                className="h-6 w-6 p-0 text-purple-600 hover:text-purple-800"
                                              >
                                                <Edit2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* Pruebas y Exámenes Section */}
                            {studentData.pruebas.length > 0 && (
                              <div className="mb-4 sm:mb-6">
                                <h4 className="text-sm sm:text-md font-medium text-indigo-800 mb-2 sm:mb-3 flex items-center">
                                  <i className="fas fa-clipboard-check mr-2 text-sm"></i>
                                  <span>Pruebas y Exámenes ({studentData.pruebas.length})</span>
                                </h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 ml-2 sm:ml-4">
                                  {studentData.pruebas
                                    .sort((a, b) => new Date(b.fecha_calificacion) - new Date(a.fecha_calificacion))
                                    .map((calificacion) => {
                                      const status = getGradeStatus(calificacion.calificacion);
                                      return (
                                        <div key={calificacion.id} className="p-2 sm:p-3 bg-indigo-50 border-2 border-indigo-300 rounded-lg">
                                          <div className="flex items-start justify-between mb-2 gap-2">
                                            <div className="flex-1 min-w-0">
                                              <h5 className="font-medium text-indigo-900 text-xs sm:text-sm flex items-center gap-1">
                                                <i className="fas fa-file-alt text-indigo-600 text-xs flex-shrink-0"></i>
                                                <span className="line-clamp-2">{calificacion.prueba_examen_titulo}</span>
                                              </h5>
                                              <Badge variant="secondary" className="text-xs bg-indigo-200 text-indigo-900 mt-1">
                                                <i className="fas fa-pencil-alt mr-1"></i>
                                                Prueba/Examen
                                              </Badge>
                                            </div>
                                            <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold flex-shrink-0 ${getGradeColor(calificacion.calificacion)}`}>
                                              {calificacion.calificacion.toFixed(1)}
                                              {calificacion.calificacion_sobre && (
                                                <span className="text-xs">/{calificacion.calificacion_sobre}</span>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {calificacion.comentarios && (
                                            <p className="text-xs text-indigo-700 mb-2 bg-white bg-opacity-50 p-2 rounded break-words">
                                              💬 {calificacion.comentarios}
                                            </p>
                                          )}
                                          
                                          <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs text-indigo-600 truncate">
                                              📅 {formatDate(calificacion.fecha_calificacion)}
                                            </p>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleEditGrade(calificacion)}
                                                className="h-6 w-6 p-0 text-indigo-600 hover:text-indigo-800"
                                              >
                                                <Edit2 className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDeleteGrade(calificacion.id, calificacion.estudiante_nombre)}
                                                className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                                title="Eliminar calificación"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* Calificaciones Manuales Section */}
                            {studentData.manuales.length > 0 && (
                              <div>
                                <h4 className="text-sm sm:text-md font-medium text-orange-800 mb-2 sm:mb-3 flex items-center">
                                  <i className="fas fa-edit mr-2 text-sm"></i>
                                  <span>Calificaciones Manuales ({studentData.manuales.length})</span>
                                </h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 ml-2 sm:ml-4">
                                  {studentData.manuales
                                    .sort((a, b) => new Date(b.fecha_calificacion) - new Date(a.fecha_calificacion))
                                    .map((calificacion) => {
                                      const status = getGradeStatus(calificacion.calificacion);
                                      return (
                                        <div key={calificacion.id} className="p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                          <div className="flex items-start justify-between mb-2 gap-2">
                                            <div className="flex-1 min-w-0">
                                              <h5 className="font-medium text-orange-900 text-xs sm:text-sm line-clamp-2">
                                                {calificacion.prueba_examen_titulo || calificacion.tarea_titulo || 'Calificación Manual'}
                                              </h5>
                                            </div>
                                            <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold flex-shrink-0 ${getGradeColor(calificacion.calificacion)}`}>
                                              {calificacion.calificacion.toFixed(1)}
                                            </div>
                                          </div>
                                          
                                          {calificacion.comentarios && (
                                            <p className="text-xs text-orange-700 mb-2 break-words">
                                              💬 {calificacion.comentarios}
                                            </p>
                                          )}
                                          
                                          <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs text-orange-600 truncate">
                                              📅 {formatDate(calificacion.fecha_calificacion)}
                                            </p>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleEditGrade(calificacion)}
                                                className="h-6 w-6 p-0 text-orange-600 hover:text-orange-800"
                                              >
                                                <Edit2 className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDeleteGrade(calificacion.id, calificacion.estudiante_nombre)}
                                                className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* No grades message */}
                            {studentData.formularios.length === 0 && studentData.manuales.length === 0 && studentData.pruebas.length === 0 && (
                              <div className="text-center py-6 text-gray-500">
                                <i className="fas fa-clipboard text-2xl mb-2"></i>
                                <p className="text-sm">Sin calificaciones registradas</p>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* No students message */}
                        {Object.keys(claseData.estudiantes).length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <i className="fas fa-users text-3xl mb-3"></i>
                            <p>No hay estudiantes con calificaciones en esta clase</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="shadow-lg border-0">
                <CardContent className="text-center py-12">
                  <i className="fas fa-chart-bar text-gray-300 text-6xl mb-6"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay calificaciones registradas</h3>
                  <p className="text-gray-600 mb-6">Comienza registrando las primeras calificaciones para tus estudiantes.</p>
                  <Button
                    onClick={() => setIsDialogOpen(true)}
                    className="instituto-gradient text-white btn-hover"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Registrar primera calificación
                  </Button>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      )}

      {/* Grade Visibility Controls - For Professors and Admins */}
      {(user.rol === 'admin' || user.rol === 'profesor') && (
        <Card className="shadow-lg border-0 border-l-4 border-l-blue-500 mt-6">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center text-blue-800">
              <i className="fas fa-eye mr-3 text-2xl"></i>
              Control de Visibilidad de Calificaciones
            </CardTitle>
            <p className="text-blue-600 text-sm mt-1">
              Habilita o deshabilita la visualización de calificaciones para los estudiantes por clase
            </p>
          </CardHeader>
          <CardContent className="p-6">
            {loadingClases ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {clases
                  .filter(clase => 
                    user.rol === 'admin' || 
                    clase.profesor_id === user.id ||
                    (clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id))
                  )
                  .map(clase => (
                    <div key={clase.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{clase.titulo}</h4>
                        <p className="text-sm text-gray-600">
                          {clase.categoria === 'estudiantes' ? 'Estudiantes' : 'Profesores'} • 
                          {clase.semestre ? ` Semestre ${clase.semestre}` : ''} • 
                          {clase.estudiantes_inscritos?.length || 0} inscritos
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${clase.calificaciones_visibles ? 'text-green-600' : 'text-red-600'}`}>
                          {clase.calificaciones_visibles ? 'Visible' : 'Oculto'}
                        </span>
                        
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={clase.calificaciones_visibles !== false} // Default to true if undefined
                            onChange={() => toggleCalificacionesVisibility(clase.id, clase.calificaciones_visibles !== false)}
                            disabled={togglingVisibility === clase.id}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600">
                          </div>
                          
                          {togglingVisibility === clase.id && (
                            <div className="ml-2">
                              <i className="fas fa-spinner fa-spin text-blue-600"></i>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  ))}
                  
                {clases.filter(clase => user.rol === 'admin' || clase.profesor_id === user.id).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <i className="fas fa-chalkboard-teacher text-3xl mb-3"></i>
                    <p>No hay clases para gestionar</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Modal de Edición Completa */}
      <Dialog open={editingGrade !== null} onOpenChange={(open) => !open && cancelEdit()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent flex items-center">
              <Edit2 className="mr-2 h-6 w-6 text-orange-600" />
              Editar Calificación Manual
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Clase Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Label className="flex items-center text-blue-900 font-semibold mb-2">
                <i className="fas fa-graduation-cap mr-2 text-blue-600"></i>
                Clase <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select
                value={editData.clase_id}
                onValueChange={(value) => {
                  setEditData({ ...editData, clase_id: value });
                  loadEstudiantesClase(value);
                }}
              >
                <SelectTrigger className="bg-white border-blue-300">
                  <SelectValue placeholder="Selecciona una clase" />
                </SelectTrigger>
                <SelectContent>
                  {clases.map((clase) => (
                    <SelectItem key={clase.id} value={clase.id}>
                      {clase.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-600 mt-1">Selecciona la clase para esta calificación</p>
            </div>

            {/* Estudiante Section */}
            {editData.clase_id && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <Label className="flex items-center text-purple-900 font-semibold mb-2">
                  <i className="fas fa-user-graduate mr-2 text-purple-600"></i>
                  Estudiante <span className="text-red-500 ml-1">*</span>
                </Label>
                <Select
                  value={editData.estudiante_id}
                  onValueChange={(value) => setEditData({ ...editData, estudiante_id: value })}
                >
                  <SelectTrigger className="bg-white border-purple-300">
                    <SelectValue placeholder="Selecciona un estudiante" />
                  </SelectTrigger>
                  <SelectContent>
                    {estudiantesClase.map((estudiante) => (
                      <SelectItem key={estudiante.id} value={estudiante.id}>
                        {estudiante.nombre} {estudiante.apellido}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {estudiantesClase.length > 0 && (
                  <p className="text-xs text-purple-600 mt-1">
                    Estudiantes inscritos en esta clase ({estudiantesClase.length})
                  </p>
                )}
              </div>
            )}

            {/* Tarea/Formulario Section (Optional) */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Label className="flex items-center text-amber-900 font-semibold mb-2">
                <i className="fas fa-tasks mr-2 text-amber-600"></i>
                Tarea o Formulario (Opcional)
              </Label>
              <Select
                value={editData.tarea_id || 'ninguna'}
                onValueChange={(value) => setEditData({ ...editData, tarea_id: value === 'ninguna' ? '' : value })}
              >
                <SelectTrigger className="bg-white border-amber-300">
                  <SelectValue placeholder="Sin tarea/formulario específico" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ninguna">Sin tarea/formulario específico</SelectItem>
                  {tareas.filter(t => t.clase_id === editData.clase_id).map((tarea) => (
                    <SelectItem key={tarea.id} value={tarea.id}>
                      📝 {tarea.titulo}
                    </SelectItem>
                  ))}
                  {formularios.filter(f => f.clase_id === editData.clase_id).map((formulario) => (
                    <SelectItem key={formulario.id} value={formulario.id}>
                      📋 {formulario.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-amber-600 mt-1">Opcional: Asociar con una tarea o formulario específico</p>
            </div>

            {/* Calificación Section */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-300 rounded-lg p-4">
              <Label className="flex items-center text-green-900 font-semibold mb-2">
                <i className="fas fa-star mr-2 text-green-600"></i>
                Calificación <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="flex items-center space-x-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editData.calificacion}
                  onChange={(e) => setEditData({ ...editData, calificacion: e.target.value })}
                  className="text-xl font-bold text-center border-green-400 focus:border-green-600"
                  placeholder="0-100"
                />
                <span className="text-gray-500 font-medium">/100</span>
              </div>
              {editData.calificacion && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    parseFloat(editData.calificacion) >= 90 ? 'bg-green-500 text-white' :
                    parseFloat(editData.calificacion) >= 70 ? 'bg-blue-500 text-white' :
                    parseFloat(editData.calificacion) >= 50 ? 'bg-yellow-500 text-white' :
                    'bg-red-500 text-white'
                  }`}>
                    {parseFloat(editData.calificacion) >= 90 ? '🌟 Excelente' :
                     parseFloat(editData.calificacion) >= 70 ? '👍 Bueno' :
                     parseFloat(editData.calificacion) >= 50 ? '📚 Regular' :
                     '❌ Insuficiente'}
                  </span>
                </div>
              )}
            </div>

            {/* Comentarios Section */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <Label className="flex items-center text-slate-900 font-semibold mb-2">
                <i className="fas fa-comment-alt mr-2 text-slate-600"></i>
                Comentarios
              </Label>
              <Textarea
                value={editData.comentarios}
                onChange={(e) => setEditData({ ...editData, comentarios: e.target.value })}
                placeholder="Observaciones, comentarios o feedback para el estudiante..."
                rows={3}
                className="border-slate-300"
              />
              <p className="text-xs text-slate-500 mt-1">Opcional: Agrega comentarios sobre el desempeño</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={cancelEdit}
              disabled={updating}
            >
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
            <Button
              onClick={() => handleUpdateGrade(editingGrade)}
              disabled={updating || !editData.calificacion || !editData.estudiante_id || !editData.clase_id}
              className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
            >
              {updating ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Actualizando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para registrar capacitación con horas */}
      <Dialog open={modalCapacitacion !== null} onOpenChange={() => setModalCapacitacion(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-user-graduate text-green-600"></i>
              Registrar Capacitación
            </DialogTitle>
          </DialogHeader>
          {modalCapacitacion && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Profesor:</p>
                <p className="font-semibold text-green-800">{modalCapacitacion.profesorNombre}</p>
                <p className="text-sm text-gray-600 mt-2">Calificación:</p>
                <p className="font-semibold text-green-800">{modalCapacitacion.promedio}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ¿Cuántas horas duró la capacitación?
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={horasCapacitacion}
                  onChange={(e) => setHorasCapacitacion(e.target.value)}
                  placeholder="Ej: 2"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  autoFocus
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setModalCapacitacion(null)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRegistrarCapacitacionPerfil}
                  disabled={registrandoCapacitacion || !horasCapacitacion}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                >
                  {registrandoCapacitacion ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Registrando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      Registrar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación antes de enviar carta de advertencia */}
      <Dialog open={confirmCarta !== null} onOpenChange={(open) => { if (!open) setConfirmCarta(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="modal-confirmar-carta">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-envelope text-amber-600"></i>
              Confirmar envío de carta
            </DialogTitle>
          </DialogHeader>
          {confirmCarta && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <p className="text-sm text-gray-700">Se enviará una <b>carta de advertencia académica</b> por email al estudiante:</p>
                <div className="mt-3 space-y-1">
                  <p className="text-sm"><span className="text-gray-500">Estudiante:</span> <b>{confirmCarta.estudianteNombre}</b></p>
                  {confirmCarta.email && (
                    <p className="text-sm"><span className="text-gray-500">Email:</span> <b className="text-amber-800">{confirmCarta.email}</b></p>
                  )}
                  <p className="text-sm"><span className="text-gray-500">Clase:</span> <b>{confirmCarta.claseNombre}</b></p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Podrás ver si el estudiante abrió la carta en el botón "Cartas enviadas".
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmCarta(null)}
                  disabled={enviandoCarta}
                  className="flex-1"
                  data-testid="btn-cancelar-envio-carta"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmarEnvioCarta}
                  disabled={enviandoCarta}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  data-testid="btn-confirmar-envio-carta"
                >
                  {enviandoCarta ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Enviando...</>
                  ) : (
                    <><i className="fas fa-paper-plane mr-2"></i>Enviar email</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal con historial de cartas enviadas y estado de apertura */}
      <Dialog open={historialCartas !== null} onOpenChange={(open) => { if (!open) setHistorialCartas(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="modal-historial-cartas">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-inbox text-amber-600"></i>
              Cartas enviadas
            </DialogTitle>
          </DialogHeader>
          {historialCartas && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <p><b>{historialCartas.estudianteNombre}</b> — {historialCartas.claseNombre}</p>
              </div>
              {historialCartas.items.length === 0 ? (
                <p className="text-sm text-gray-500">No hay cartas enviadas todavía.</p>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {historialCartas.items.map((c) => {
                    const abierta = c.tracking_estado === 'abierto';
                    const fechaEnv = c.fecha_generacion ? new Date(c.fecha_generacion).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' }) : '—';
                    const fechaAp = c.fecha_apertura ? new Date(c.fecha_apertura).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' }) : null;
                    return (
                      <div key={c.id} className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`carta-item-${c.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="text-sm">
                            <p><i className="fas fa-calendar-alt text-gray-400 mr-1"></i> {fechaEnv}</p>
                            <p className="text-xs text-gray-500 mt-1">Promedio al momento: <b>{c.promedio}%</b></p>
                            {c.generado_por_nombre && (
                              <p className="text-xs text-gray-500">Enviada por: {c.generado_por_nombre}</p>
                            )}
                          </div>
                          <div className="text-right text-xs">
                            {abierta ? (
                              <>
                                <Badge className="bg-green-100 text-green-800 border-green-200">
                                  <i className="fas fa-eye mr-1"></i>Abierta
                                </Badge>
                                {fechaAp && <p className="text-gray-500 mt-1">Abierta: {fechaAp}</p>}
                                {c.aperturas > 1 && <p className="text-gray-500">{c.aperturas} aperturas</p>}
                              </>
                            ) : c.tracking_estado === 'error' ? (
                              <Badge className="bg-red-100 text-red-800 border-red-200">
                                <i className="fas fa-exclamation-triangle mr-1"></i>Error
                              </Badge>
                            ) : c.tracking_estado === 'legacy' ? (
                              <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                                <i className="fas fa-file-pdf mr-1"></i>PDF (legacy)
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700 border-gray-200">
                                <i className="fas fa-paper-plane mr-1"></i>Enviada
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="pt-2">
                <Button
                  variant="outline"
                  onClick={() => setHistorialCartas(null)}
                  className="w-full"
                  data-testid="btn-cerrar-historial-cartas"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calificaciones;